// "Are the backups healthy?" at a glance: database dumps, nightly exports
// (next/last passage), Drive copy per institution, offsite mirror, retention.
import React from 'react';
import { Database, FileSpreadsheet, Cloud, HardDriveDownload, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react';
import type { BackupOverview } from '@/types/tenant-export.types';
import { formatBytes } from './TenantExportRunsTable';

const OFFSITE_WORKFLOW_URL = 'https://github.com/taharharkat/education/actions/workflows/offsite-backup.yml';

const fmt = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const hoursSince = (iso: string | null | undefined): number | null => (iso ? (Date.now() - new Date(iso).getTime()) / 36e5 : null);

interface BackupOverviewPanelProps {
  overview: BackupOverview | null;
}

export const BackupOverviewPanel: React.FC<BackupOverviewPanelProps> = ({ overview }) => {
  if (!overview) return null;
  const { scheduler, database, exports, retention } = overview;
  const dbAge = hoursSince(database.lastBackup?.completedAt);
  const dbStale = dbAge === null || dbAge > 30;
  const dailySchedule = database.schedules.find((s) => s.isActive);
  const lastNightly = scheduler.lastFinishedAt ?? scheduler.lastStartedAt;
  const nightlyStale = scheduler.enabled && (hoursSince(lastNightly) === null || (hoursSince(lastNightly) ?? 0) > 30);

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      <Card icon={<Database size={18} />} title="Base de données" ok={!dbStale}>
        <Line label="Dernière sauvegarde" value={fmt(database.lastBackup?.completedAt)} />
        <Line label="Taille" value={formatBytes(database.lastBackup?.bytes ?? null)} />
        <Line label="Planification" value={dailySchedule ? `${dailySchedule.name} · ${dailySchedule.frequency === 'DAILY' ? 'quotidienne' : dailySchedule.frequency.toLowerCase()} · ${dailySchedule.retentionDays} j` : 'aucune active'} />
        <Line label="Prochaine" value={fmt(dailySchedule?.nextRunAt)} />
      </Card>

      <Card icon={<FileSpreadsheet size={18} />} title="Exports nocturnes" ok={!nightlyStale}>
        <Line label="Automatique" value={scheduler.enabled ? 'oui · 04:00 UTC (05:00 Maroc)' : 'désactivé sur ce serveur'} />
        <Line label="Dernier passage" value={lastNightly ? `${fmt(lastNightly)}${scheduler.lastResult ? ` · ${scheduler.lastResult.ok} ok, ${scheduler.lastResult.failed} échec(s)` : ' · en cours'}` : 'jamais'} />
        <Line label="Prochain passage" value={fmt(scheduler.nextRunAt)} />
        <Line label="Conservation" value={`${retention.dailyRuns} jours + 1er du mois${retention.monthlyForever ? ' pour toujours' : ''}`} />
      </Card>

      <Card icon={<Cloud size={18} />} title="Copie Google Drive" ok={exports.every((e) => !e.drive?.error)}>
        {exports.length === 0 && <p className="text-xs text-gray-500">Aucune institution active.</p>}
        {exports.map((e) => (
          <Line
            key={e.tenantId}
            label={e.tenantName}
            value={e.drive ? (e.drive.error ? 'échec' : `${fmt(e.drive.syncedAt)} · ${e.documents?.listed ?? 0} docs`) : e.lastRunAt ? 'pas encore copié' : 'aucun export'}
            warn={Boolean(e.drive?.error) || (e.documents?.missing ?? 0) > 0}
          />
        ))}
      </Card>

      <Card icon={<HardDriveDownload size={18} />} title="Copie hors site (Backblaze)" ok>
        <p className="text-xs text-gray-600">
          Chaque nuit à 03:30 UTC, tout le stockage (sauvegardes, fichiers, exports) est recopié chez un second hébergeur par GitHub Actions.
        </p>
        <a href={OFFSITE_WORKFLOW_URL} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
          Voir les dernières exécutions <ExternalLink size={12} />
        </a>
      </Card>
    </div>
  );
};

const Card: React.FC<{ icon: React.ReactNode; title: string; ok: boolean; children: React.ReactNode }> = ({ icon, title, ok, children }) => (
  <div className={`rounded-xl border bg-white p-4 ${ok ? 'border-gray-200' : 'border-amber-300'}`}>
    <div className="mb-2 flex items-center justify-between">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">{icon} {title}</h3>
      {ok ? <CheckCircle size={16} className="text-emerald-500" /> : <AlertTriangle size={16} className="text-amber-500" />}
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

const Line: React.FC<{ label: string; value: string; warn?: boolean }> = ({ label, value, warn }) => (
  <div className="flex items-baseline justify-between gap-3 text-xs">
    <span className="text-gray-500">{label}</span>
    <span className={`text-right font-medium ${warn ? 'text-amber-700' : 'text-gray-800'}`}>{value}</span>
  </div>
);
